/**
 * @file RequestPropertiesAssetUpload.js
 * @description Properties panel for asset upload container request configuration.
 */

import { useEffect, useState } from 'react';
import './PropertiesMultimedia.css';
import styles from '../assets/AssetLibrary.module.scss';
import Spinner from '../ui/spinners/Spinner';
import { SearchableDropdown } from '../helpers/SearchableDropdown';
import { fetchClientAdminUsers } from '../../services/clientService';
import Toast from '../ui/toasts/Toast';
import { getText } from '../../i18n';

const ALL_CATEGORY_OPTIONS = [2, 3, 4, 5];

const FILE_TYPE_MAP = {
    1: [
        'mp4',
        'mov',
        'mp3',
        'wav',
        'jpg',
        'jpeg',
        'png',
        'svg',
        'docx',
        'doc',
        'txt',
        'pdf',
        'xls',
        'xlsx',
        'csv',
        'numbers',
    ],
    2: ['mp3', 'wav'],
    3: ['docx', 'doc', 'txt', 'pdf', 'xls', 'xlsx', 'csv', 'numbers'],
    4: ['jpg', 'jpeg', 'png', 'svg'],
    5: ['mp4', 'mov'],
};

const SORTED_OPTION_MAP = {
    2: ['mp3', 'wav'],
    3: ['csv', 'doc', 'docx', 'numbers', 'pdf', 'txt', 'xls', 'xlsx'],
    4: ['jpeg', 'jpg', 'png', 'svg'],
    5: ['mov', 'mp4'],
};

function getAllowedFileTypesForOption(option) {
    return FILE_TYPE_MAP[option] || FILE_TYPE_MAP[1];
}

function buildMergedFileTypes(options) {
    return [
        ...new Set(
            options.flatMap(option => getAllowedFileTypesForOption(option)),
        ),
    ];
}

function determineFileTypeOptions(fileTypes) {
    const sorted = [...fileTypes].sort();
    const matchedOptions = [];

    for (const [option, types] of Object.entries(SORTED_OPTION_MAP)) {
        const hasAllTypes = types.every(type => sorted.includes(type));
        if (hasAllTypes) {
            matchedOptions.push(Number(option));
        }
    }

    if (matchedOptions.length === 0) {
        return ALL_CATEGORY_OPTIONS;
    }

    return matchedOptions;
}

function isAllChecked(options) {
    return ALL_CATEGORY_OPTIONS.every(option => options.includes(option));
}

function RequestPropertiesAssetUpload({
    item,
    setItem,
    requestState,
    setRequestState,
}) {
    const [areInputsDisabled, setAreInputsDisabled] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [individualsDropdown, setIndividualsDropdown] = useState([]);

    const {
        selectedIndividuals,
        selectedAllowedFileTypeOption,
        allowedFileTypes,
        isInitialized,
    } = requestState;

    const checkedOptions = Array.isArray(selectedAllowedFileTypeOption)
        ? selectedAllowedFileTypeOption
        : [selectedAllowedFileTypeOption];

    const hasOnlyOneChecked = checkedOptions.length === 1;

    function updateRequestState(updates) {
        setRequestState(prev => ({ ...prev, ...updates }));
    }

    useEffect(() => {
        async function loadAdminUsers() {
            try {
                const fetchedClientAdminUsers = await fetchClientAdminUsers();
                const formattedAdminUsers = fetchedClientAdminUsers.map(
                    user => ({
                        id: user.individualId,
                        name: `${user.firstName} ${user.lastName}`,
                    }),
                );
                setIndividualsDropdown(formattedAdminUsers);
            } catch (error) {
                console.error('Failed to fetch individuals', error);
                Toast.error(getText('msg_b6e11cea734f'));
            }
        }

        loadAdminUsers();
    }, []);

    useEffect(() => {
        if (!isInitialized) {
            return;
        }

        setItem(prevState => {
            const { assetUploadPart } = prevState;
            const assetUploadPartId = assetUploadPart?.id ?? 0;

            return {
                ...prevState,
                assetUploadPart: {
                    ...assetUploadPart,
                    assetUploadPartSharingIndividuals: selectedIndividuals.map(
                        individual => ({
                            individualId: individual.id,
                        }),
                    ),
                    assetUploadPartAllowedFileTypes: allowedFileTypes.map(
                        fileType => ({
                            assetUploadPartId,
                            fileType,
                        }),
                    ),
                },
            };
        });
    }, [selectedIndividuals, allowedFileTypes, isInitialized, setItem]);

    useEffect(() => {
        if (isInitialized) {
            return;
        }

        async function fetchInitial() {
            setIsLoading(true);

            if (item === undefined) {
                updateRequestState({
                    selectedAllowedFileTypeOption: [...ALL_CATEGORY_OPTIONS],
                    allowedFileTypes: getAllowedFileTypesForOption(1),
                    isInitialized: true,
                });
                setIsLoading(false);
                return;
            }

            if ('templateStatus' in item && item.templateStatus === -1) {
                setAreInputsDisabled(true);
            }

            const assetUploadPart = item.assetUploadPart || {};

            let tempSelectedIndividuals = [];
            if (assetUploadPart.assetUploadPartSharingIndividuals) {
                tempSelectedIndividuals =
                    assetUploadPart.assetUploadPartSharingIndividuals.map(
                        individual => ({
                            id: individual.individualId,
                        }),
                    );
            }

            let tempAllowedFileTypeOption = [...ALL_CATEGORY_OPTIONS];
            let tempAllowedFileTypes = getAllowedFileTypesForOption(1);

            if (assetUploadPart.assetUploadPartAllowedFileTypes?.length > 0) {
                const savedFileTypes =
                    assetUploadPart.assetUploadPartAllowedFileTypes.map(
                        ft => ft.fileType,
                    );
                tempAllowedFileTypeOption =
                    determineFileTypeOptions(savedFileTypes);
                tempAllowedFileTypes = savedFileTypes;
            }

            updateRequestState({
                selectedIndividuals: tempSelectedIndividuals,
                selectedAllowedFileTypeOption: tempAllowedFileTypeOption,
                allowedFileTypes: tempAllowedFileTypes,
                isInitialized: true,
            });

            setIsLoading(false);
        }

        fetchInitial();
    }, [isInitialized, item]);

    function handleIndividualsChange(selectedOptions) {
        updateRequestState({ selectedIndividuals: selectedOptions });
    }

    function handleAllCheckboxChange() {
        if (isAllChecked(checkedOptions)) {
            return;
        }

        updateRequestState({
            selectedAllowedFileTypeOption: [...ALL_CATEGORY_OPTIONS],
            allowedFileTypes: getAllowedFileTypesForOption(1),
        });
    }

    function handleFileTypeCheckboxChange(option) {
        const isCurrentlySelected = checkedOptions.includes(option);

        if (isCurrentlySelected && hasOnlyOneChecked) {
            return;
        }

        let updatedOptions;
        if (isCurrentlySelected) {
            updatedOptions = checkedOptions.filter(o => o !== option);
        } else {
            updatedOptions = [...checkedOptions, option];
        }

        updateRequestState({
            selectedAllowedFileTypeOption: updatedOptions,
            allowedFileTypes: buildMergedFileTypes(updatedOptions),
        });
    }

    function isFileTypeOptionChecked(option) {
        return checkedOptions.includes(option);
    }

    function isCategoryDisabled(option) {
        if (areInputsDisabled) {
            return true;
        }

        if (hasOnlyOneChecked && checkedOptions.includes(option)) {
            return true;
        }

        return false;
    }

    if (isLoading) {
        return <Spinner width={50} className={styles.loadingSpinner} />;
    }

    return (
        <>
            <div
                style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    marginBottom: '20px',
                }}
            >
                <div
                    style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        color: 'rgba(47, 43, 67, 1)',
                    }}
                >
                    {getText('msg_6b2ae41c6519')}
                </div>
            </div>

            <div
                style={{
                    width: '100%',
                    minWidth: '100%',
                    gap: '24px',
                    display: 'flex',
                }}
            >
                <div className="form-check">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        name="fileTypeAll"
                        id="fileTypeAll"
                        value="all"
                        checked={isAllChecked(checkedOptions)}
                        onChange={handleAllCheckboxChange}
                        disabled={
                            areInputsDisabled || isAllChecked(checkedOptions)
                        }
                    />
                    <label className="form-check-label" htmlFor="fileTypeAll">
                        {getText('msg_6a72085653e4')}
                    </label>
                </div>
                <div className="form-check">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        name="fileTypeAudio"
                        id="fileTypeAudio"
                        value="audio"
                        checked={isFileTypeOptionChecked(2)}
                        onChange={() => handleFileTypeCheckboxChange(2)}
                        disabled={isCategoryDisabled(2)}
                    />
                    <label className="form-check-label" htmlFor="fileTypeAudio">
                        {getText('msg_acdac205f5e2')}
                    </label>
                </div>
                <div className="form-check">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        name="fileTypeDocument"
                        id="fileTypeDocument"
                        value="document"
                        checked={isFileTypeOptionChecked(3)}
                        onChange={() => handleFileTypeCheckboxChange(3)}
                        disabled={isCategoryDisabled(3)}
                    />
                    <label
                        className="form-check-label"
                        htmlFor="fileTypeDocument"
                    >
                        {getText('msg_e214b8a29923')}
                    </label>
                </div>
                <div className="form-check">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        name="fileTypeImage"
                        id="fileTypeImage"
                        value="image"
                        checked={isFileTypeOptionChecked(4)}
                        onChange={() => handleFileTypeCheckboxChange(4)}
                        disabled={isCategoryDisabled(4)}
                    />
                    <label className="form-check-label" htmlFor="fileTypeImage">
                        {getText('msg_50e19fda0d5b')}
                    </label>
                </div>
                <div className="form-check">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        name="fileTypeVideo"
                        id="fileTypeVideo"
                        value="video"
                        checked={isFileTypeOptionChecked(5)}
                        onChange={() => handleFileTypeCheckboxChange(5)}
                        disabled={isCategoryDisabled(5)}
                    />
                    <label className="form-check-label" htmlFor="fileTypeVideo">
                        {getText('msg_bc17c1f0179a')}
                    </label>
                </div>
            </div>

            <hr
                style={{
                    color: 'rgba(217, 217, 217, 1)',
                }}
            />

            <div
                style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: 'rgba(47, 43, 67, 1)',
                    marginBottom: '8px',
                }}
            >
                {getText('msg_e433ed5210f9')}
                <span
                    style={{
                        color: '#EF4E45',
                        marginLeft: '4px',
                        fontWeight: '500',
                    }}
                >
                    *
                </span>
            </div>
            <div
                style={{
                    fontSize: '14px',
                    fontWeight: '400',
                    textAlign: 'left',
                    color: 'rgba(47, 43, 67, 0.6)',
                    marginBottom: '16px',
                }}
            >
                {getText('msg_353c504dd1ee')}
            </div>
            <SearchableDropdown
                placeholderLabel={getText('msg_b1f168d000c5')}
                options={individualsDropdown}
                optionIDField="id"
                savedOptions={selectedIndividuals}
                savedOptionIDField="id"
                updateOptions={handleIndividualsChange}
                disabled={areInputsDisabled}
                showTags
            />
        </>
    );
}

export default RequestPropertiesAssetUpload;
